/**
 * MOBY Cart Drawer
 * Reads Liquid values from data-* attributes on #moby-cart-drawer element.
 */
  (function() {
    const drawer = document.getElementById('moby-cart-drawer');
    if (!drawer) return;

    // Cache focus-trap module for keyboard accessibility. Keep the promise so
    // opening the drawer before the dynamic import resolves is still safe.
    let focusModule = null;
    const focusModulePromise = import('@theme/focus')
      .then(function(module) {
        focusModule = module;
        return module;
      })
      .catch(function(error) {
        console.error('Cart drawer focus module failed to load:', error);
        return null;
      });
    let drawerOpener = null;

    function setPageInert(isInert) {
      Array.from(document.body.children).forEach(function(element) {
        if (!(element instanceof HTMLElement) || element.contains(drawer)) return;
        if (isInert) {
          if (!element.inert) {
            element.inert = true;
            element.dataset.mobyCartInert = 'true';
          }
        } else if (element.dataset.mobyCartInert === 'true') {
          element.inert = false;
          delete element.dataset.mobyCartInert;
        }
      });
    }

    async function applyDrawerFocusTrap(moveFocus) {
      const module = focusModule || await focusModulePromise;
      if (!drawer.classList.contains('is-open')) return;
      const panel = drawer.querySelector('.moby-cart-drawer__panel');
      if (!panel) return;
      if (module && typeof module.trapFocus === 'function') {
        module.trapFocus(panel);
      } else if (moveFocus !== false) {
        panel.focus();
      }
      if (moveFocus !== false) {
        const closeButton = panel.querySelector('[data-cart-drawer-close]');
        if (closeButton) closeButton.focus();
      }
    }

    function restoreFocusToOpener() {
      if (drawerOpener && document.contains(drawerOpener) && typeof drawerOpener.focus === 'function') {
        drawerOpener.focus();
      }
      drawerOpener = null;
    }

    function captureDrawerFocusState() {
      const active = document.activeElement;
      if (!(active instanceof HTMLElement) || !drawer.contains(active)) return null;
      const item = active.closest('[data-cart-item]');
      const actionAttributes = [
        'data-quantity-minus',
        'data-quantity-plus',
        'data-remove-item',
        'data-cart-checkout',
        'data-cart-drawer-close'
      ];
      const action = actionAttributes.find(function(attribute) { return active.hasAttribute(attribute); });
      return {
        lineId: item ? item.dataset.lineId : '',
        action: action || '',
        wasPanel: active.classList.contains('moby-cart-drawer__panel')
      };
    }

    function restoreDrawerFocusState(state) {
      if (!state) return;
      let scope = drawer;
      if (state.lineId) {
        const matchingItem = Array.from(drawer.querySelectorAll('[data-cart-item]')).find(function(item) {
          return item.dataset.lineId === state.lineId;
        });
        if (matchingItem) scope = matchingItem;
      }
      const target = state.action ? scope.querySelector('[' + state.action + ']') : null;
      if (target && typeof target.focus === 'function') {
        target.focus();
      } else if (state.wasPanel) {
        const panel = drawer.querySelector('.moby-cart-drawer__panel');
        if (panel) panel.focus();
      }
    }

    // Get thresholds from section wrapper
    const sectionWrapper = document.getElementById('cart-drawer-section');
    let shippingThresholds = { SEK: 500, EUR: 43, USD: 50, GBP: 40, NOK: 500, DKK: 350, CAD: 69, PLN: 199, CZK: 1100, HUF: 18000, RON: 229, BGN: 89 };
    let currentCurrency = drawer.dataset.currency || 'SEK';
    let currentCountry = '';
    let moneyFormat = drawer.dataset.moneyFormat || '';
    let freeShippingPromo = false;
    let thresholdUpsellState = null;
    let renderedWriteCount = 0;
    let renderingWriteCount = null;
    let renderSeq = 0;
    let renderFailed = false;
    let isProcessing = false;
    let checkoutConsistencyError = false;
    let confirmedPriceSignature = null;
    let checkoutErrorMessage = '';
    let checkoutErrorPersistent = false;

    if (sectionWrapper) {
      try {
        const thresholdsData = sectionWrapper.dataset.shippingThresholds;
        if (thresholdsData) {
          shippingThresholds = JSON.parse(thresholdsData);
        }
        if (sectionWrapper.dataset.currency) {
          currentCurrency = sectionWrapper.dataset.currency;
        }
        currentCountry = (sectionWrapper.dataset.countryCode || '').toUpperCase();
        // Check if free shipping promo is active
        freeShippingPromo = sectionWrapper.dataset.freeShippingPromo === 'true';
      } catch (e) {
        console.error('Error parsing shipping thresholds:', e);
      }
    }

    const priceLocale = drawer.dataset.locale || 'en';

    // Format money based on currency
    function formatMoney(cents) {
      const amount = cents / 100;

      // Currency-specific formatting
      const formatters = {
        'SEK': (a) => a.toLocaleString(priceLocale, { minimumFractionDigits: Number.isInteger(a) ? 0 : 2, maximumFractionDigits: 2 }) + ' kr',
        'NOK': (a) => a.toLocaleString(priceLocale, { minimumFractionDigits: Number.isInteger(a) ? 0 : 2, maximumFractionDigits: 2 }) + ' kr',
        'DKK': (a) => a.toLocaleString(priceLocale, { minimumFractionDigits: Number.isInteger(a) ? 0 : 2, maximumFractionDigits: 2 }) + ' kr',
        'EUR': (a) => '€' + a.toFixed(2),
        'USD': (a) => '$' + a.toFixed(2),
        'GBP': (a) => '£' + a.toFixed(2)
      };

      if (formatters[currentCurrency]) {
        return formatters[currentCurrency](amount);
      }

      // Fallback
      return amount.toFixed(2) + ' ' + currentCurrency;
    }

    function formatPerServing(unitPriceCents) {
      // The label must come from the drawer's rendered locale — the
      // library's built-in fallback is hardcoded English and used to
      // flip "per portion" to "per serving" after quantity changes.
      const perServingLabel = drawer.dataset.textPerServing || 'per serving';
      if (typeof window.MobyPriceFormat === 'undefined' || typeof window.MobyPriceFormat.formatPerServing !== 'function') {
        const amount = unitPriceCents / 100;
        if (['SEK', 'NOK', 'DKK'].includes(currentCurrency)) {
          return (amount / 30).toFixed(2).replace('.', ',') + ' kr ' + perServingLabel;
        }
        return amount.toFixed(2);
      }
      return window.MobyPriceFormat.formatPerServing(unitPriceCents, {
        currency: currentCurrency,
        locale: priceLocale,
        label: ' ' + perServingLabel
      });
    }

    function updatePerServingPrices() {
      drawer.querySelectorAll('[data-price-per-serving-cents]').forEach((priceEl) => {
        const unitPriceCents = parseInt(priceEl.dataset.pricePerServingCents, 10);
        if (!unitPriceCents) return;
        priceEl.textContent = formatPerServing(unitPriceCents);
      });
    }

    // Update shipping bar display
    function updateShippingBar(cartTotal, itemCount, cart) {
      const shippingBar = drawer.querySelector('[data-shipping-bar]');
      const shippingFill = drawer.querySelector('[data-shipping-fill]');
      const shippingText = drawer.querySelector('[data-shipping-text]');

      // Subscription reassurance line under the checkout button: visible
      // only while the cart holds a selling-plan item. The section
      // re-render handles this in Liquid; this toggle covers the in-place
      // update path between re-renders.
      const subReassurance = drawer.querySelector('[data-sub-reassurance]');
      if (subReassurance) {
        const hasSub = !!(cart && cart.items && cart.items.some(item => item.selling_plan_allocation));
        subReassurance.hidden = !hasSub || itemCount === 0;
      }

      if (!shippingBar || !shippingFill || !shippingText) return;

      // Show/hide based on item count
      shippingBar.style.display = itemCount > 0 ? 'flex' : 'none';

      if (itemCount === 0) return;

      // If free shipping promo is active, always show free shipping
      if (freeShippingPromo) {
        shippingFill.style.width = '100%';
        shippingText.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg> ' + (drawer.dataset.textFreeAll || 'Free shipping on all orders!');
        shippingText.classList.add('moby-cart-drawer__shipping-text--success');
        return;
      }

      // If cart contains a subscription, show subscription-specific copy
      // (subscriptions always ship free in Sweden). Distinct text from the
      // promo-all-orders state so we don't make a claim that isn't true
      // for the rest of the catalog.
      if (currentCountry === 'SE' && cart && cart.items && cart.items.some(item => item.selling_plan_allocation)) {
        shippingFill.style.width = '100%';
        shippingText.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg> ' + ((drawer && drawer.dataset.textSubscription) || 'Free shipping included with your subscription');
        shippingText.classList.add('moby-cart-drawer__shipping-text--success');
        return;
      }

      // Get threshold for current currency (in base currency units, not cents)
      const threshold = shippingThresholds[currentCurrency] || 500;
      const thresholdCents = threshold * 100;

      // Calculate progress
      const progress = Math.min((cartTotal / thresholdCents) * 100, 100);
      const remaining = thresholdCents - cartTotal;

      // Update fill bar
      shippingFill.style.width = progress + '%';

      // Update text
      if (remaining > 0) {
        shippingText.innerHTML = (drawer.dataset.textRemaining || 'Add <strong>AMOUNT_PLACEHOLDER</strong> more for free shipping').replace('AMOUNT_PLACEHOLDER', formatMoney(remaining));
        shippingText.classList.remove('moby-cart-drawer__shipping-text--success');
      } else {
        shippingText.innerHTML = '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg> ' + (drawer.dataset.textQualify || 'You qualify for free shipping!');
        shippingText.classList.add('moby-cart-drawer__shipping-text--success');
      }
    }

    function updateThresholdUpsell(cart) {
      const upsell = drawer.querySelector('[data-threshold-upsell]');
      const title = drawer.querySelector('[data-threshold-upsell-title]');
      const body = drawer.querySelector('[data-threshold-upsell-body]');
      const button = drawer.querySelector('[data-threshold-upsell-btn]');

      thresholdUpsellState = null;

      if (!upsell || !title || !body || !button || !cart || freeShippingPromo || !cart.item_count) {
        if (upsell) upsell.hidden = true;
        return;
      }

      // The purchase-type card on each line now carries the subscription
      // offer, so this quantity banner would be a second, competing deal
      // above it. The automatic TAKE2/TAKE3 discounts still apply when a
      // shopper stocks up on their own — they are simply no longer
      // advertised at the moment of decision.
      upsell.hidden = true;
      return;

      // Tiered discount logic: TAKE2 (2+ boxes = 10%), TAKE3 (3+ boxes = 15%)
      var totalOneTimeQty = 0;
      var bestItem = null;

      cart.items.forEach(function(item, index) {
        if (!item.selling_plan_allocation && item.quantity > 0) {
          totalOneTimeQty += item.quantity;
          if (!bestItem) {
            bestItem = { line: index + 1, quantity: item.quantity, title: item.product_title || item.title || 'this item' };
          }
        }
      });

      if (!bestItem) {
        upsell.hidden = true;
        return;
      }

      var msg = '';
      var btnText = '';
      var addQty = 0;

      if (totalOneTimeQty === 1) {
        // At 1 box → upsell to 2 (10% off + free shipping)
        msg = (drawer && drawer.dataset.textUpsell2) || 'Add 1 more — get 10% off + free shipping';
        btnText = (drawer && drawer.dataset.textUpsellBtn) || 'Add 1 more';
        addQty = 1;
      } else if (totalOneTimeQty === 2) {
        // At 2 boxes → upsell to 3 (15% off)
        msg = (drawer && drawer.dataset.textUpsell3) || 'Got room for one more? — upgrade to 15% off';
        btnText = (drawer && drawer.dataset.textUpsellBtn) || 'Add 1 more';
        addQty = 1;
      } else {
        // 3+ boxes → already at max tier
        upsell.hidden = true;
        return;
      }

      thresholdUpsellState = {
        line: bestItem.line,
        title: bestItem.title,
        targetQuantity: bestItem.quantity + addQty,
        boxesNeeded: addQty
      };

      title.textContent = msg;
      body.textContent = '';
      button.textContent = btnText;
      button.disabled = false;
      upsell.hidden = false;
    }

    // Show skeleton loading state
    function showSkeleton() {
      const skeleton = drawer.querySelector('[data-cart-skeleton]');
      const itemsWrapper = drawer.querySelector('[data-cart-items-wrapper]');
      const content = drawer.querySelector('[data-cart-drawer-content]');

      if (skeleton) skeleton.style.display = 'flex';
      if (itemsWrapper) itemsWrapper.style.display = 'none';
      if (content) content.classList.add('is-loading');
    }

    // Hide skeleton loading state
    function hideSkeleton() {
      const skeleton = drawer.querySelector('[data-cart-skeleton]');
      const itemsWrapper = drawer.querySelector('[data-cart-items-wrapper]');
      const content = drawer.querySelector('[data-cart-drawer-content]');

      if (skeleton) skeleton.style.display = 'none';
      if (itemsWrapper) itemsWrapper.style.display = 'block';
      if (content) content.classList.remove('is-loading');
      const countText = drawer.querySelector('[data-cart-count-text]');
      if (countText) countText.style.visibility = '';
    }

    // Pending is in-flight only. A finished reconcile (success or error) must
    // not leave Betala aria-disabled — checkoutConsistencyError is handled
    // separately so the banner can stay without stranding the CTA.
    // While gated, the drawer's checkout control shows the localized checking
    // text instead of a price that may still change, at its current size. Its
    // ready label is kept and put back when released. A re-render brings a new
    // control with the authoritative label, which is the one restored later.
    function setCheckoutLabelPending(control, pending) {
      if (typeof control.matches !== 'function' || !control.matches('[data-cart-checkout]') || !('innerHTML' in control)) return;
      if (pending) {
        if (control.mobyReadyHtml != null) return;
        control.mobyReadyHtml = control.innerHTML;
        if (control.offsetWidth) control.style.minWidth = control.offsetWidth + 'px';
        if (control.offsetHeight) control.style.minHeight = control.offsetHeight + 'px';
        const text = (typeof drawer !== 'undefined' && drawer && drawer.dataset.textCheckingCart) || 'Checking your cart…';
        control.innerHTML = '';
        const label = document.createElement('span');
        label.setAttribute('data-checkout-pending-label', '');
        label.textContent = text;
        control.appendChild(label);
        control.setAttribute('aria-busy', 'true');
      } else if (control.mobyReadyHtml != null) {
        control.innerHTML = control.mobyReadyHtml;
        control.mobyReadyHtml = null;
        control.style.minWidth = '';
        control.style.minHeight = '';
        control.removeAttribute('aria-busy');
      }
    }

    function setCheckoutPending(pending) {
      const disabled = Boolean(pending);
      const checkoutControls = document.querySelectorAll('[data-cart-checkout], button[name="checkout"], a[href="/checkout"]');
      checkoutControls.forEach(function(control) {
        setCheckoutLabelPending(control, disabled);
        if (disabled) {
          control.setAttribute('aria-disabled', 'true');
          control.classList.add('is-pending');
          if ('disabled' in control && !control.disabled) {
            control.disabled = true;
            control.dataset.mobyCheckoutDisabled = 'true';
          }
        } else {
          control.removeAttribute('aria-disabled');
          control.classList.remove('is-pending');
          if ('disabled' in control && control.dataset.mobyCheckoutDisabled === 'true') {
            control.disabled = false;
            delete control.dataset.mobyCheckoutDisabled;
          }
        }
      });

      document.querySelectorAll('shopify-accelerated-checkout-cart, [data-shopify-accelerated-checkout]').forEach(function(control) {
        if (disabled) {
          if (!control.inert) {
            control.inert = true;
            control.dataset.mobyCheckoutInert = 'true';
          }
        } else if (control.dataset.mobyCheckoutInert === 'true') {
          control.inert = false;
          delete control.dataset.mobyCheckoutInert;
        }
      });
      if (!pending) protectAcceleratedCheckout();
      if (typeof renderSubscriptionSwitch === 'function') renderSubscriptionSwitch();
    }

    function protectAcceleratedCheckout() {
      if (!checkoutConsistencyError) return;
      document.querySelectorAll('shopify-accelerated-checkout-cart, [data-shopify-accelerated-checkout]').forEach(function(control) {
        if (!control.inert) {
          control.inert = true;
          control.dataset.mobyCheckoutInert = 'true';
        }
      });
    }

    function resolveSellingPlanIds() {
      const itemWithPlans = Array.from(
        drawer.querySelectorAll('[data-cart-item][data-upgrade-plan-id]')
      ).find(function(item) {
        return item.dataset.upgradePlanId || item.dataset.fallbackPlanId;
      });
      const threeMonthOption = document.querySelector('[data-option="subscribe-3"][data-selling-plan-id]');
      const oneMonthOption = document.querySelector('[data-option="subscribe"][data-selling-plan-id]');
      const plan3boxId = drawer.dataset.plan3boxId ||
        (itemWithPlans && itemWithPlans.dataset.upgradePlanId) ||
        (threeMonthOption && threeMonthOption.dataset.sellingPlanId) || '';
      const plan1boxId = drawer.dataset.plan1boxId ||
        (itemWithPlans && itemWithPlans.dataset.fallbackPlanId) ||
        (oneMonthOption && oneMonthOption.dataset.sellingPlanId) || '';

      // Persist the exact IDs once discovered so cart events that follow a
      // section replacement do not fall back to an empty-cart configuration.
      if (plan3boxId) drawer.dataset.plan3boxId = plan3boxId;
      if (plan1boxId) drawer.dataset.plan1boxId = plan1boxId;
      return { plan3boxId: plan3boxId, plan1boxId: plan1boxId, singleBoxPlanIds: new Set((drawer.dataset.singleBoxPlanIds || plan1boxId).split(',').map(function(id) { return id.trim(); }).filter(Boolean)) };
    }

    // Welcome Kit reconciliation is bidirectional and idempotent. Eligible
    // subscriptions receive each configured gift exactly once; carts without
    // an eligible line have all hidden gifts removed. Runs only inside the
    // coordinator's settle job: every write goes through the job context.
    async function reconcileGiftLines(ctx, cart) {
      if (!cart || !Array.isArray(cart.items) || !window.MobyIsGiftLine) return { cart: cart, failed: false };
      const giftLines = cart.items.filter(function(item) { return window.MobyIsGiftLine(item); });
      const resolvedPlanIds = resolveSellingPlanIds();
      const eligibleProductId = drawer.dataset.eligibleProductId;
      const entitledIds = new Set();
      const configuredIds = function(value) { return (value || '').split(',').map(function(id) { return id.trim(); }).filter(Boolean); };
      cart.items.forEach(function(item) {
        if (String(item.product_id) !== String(eligibleProductId) || window.MobyIsGiftLine(item)) return;
        const planId = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan && item.selling_plan_allocation.selling_plan.id;
        let ids = [];
        if (!planId) ids = configuredIds(drawer.dataset.giftOnetimeIds);
        else if (resolvedPlanIds.singleBoxPlanIds.has(String(planId))) ids = configuredIds(drawer.dataset.giftMonthlyIds);
        else if (String(planId) === String(resolvedPlanIds.plan3boxId) && item.quantity >= 3) ids = configuredIds(drawer.dataset.giftVariantIds);
        ids.forEach(function(id) { entitledIds.add(id); });
      });

      // Keep exactly one of each entitled gift. Duplicate gift lines,
      // quantities above one, retired gift variants and gifts the cart is no
      // longer entitled to are corrected before anything missing is added.
      const updates = {};
      const existingGiftIds = new Set();
      giftLines.forEach(function(item) {
        const variantId = String(item.variant_id);
        if (!entitledIds.has(variantId) || existingGiftIds.has(variantId)) {
          updates[item.key] = 0;
          return;
        }
        existingGiftIds.add(variantId);
        if (item.quantity !== 1) updates[item.key] = 1;
      });
      if (Object.keys(updates).length) {
        cart = await ctx.post('update', { updates: updates }, { type: 'updates', updates: updates });
      }

      const missingGiftIds = Array.from(entitledIds).filter(function(id) { return !existingGiftIds.has(String(id)); });
      let remainingGiftIds = missingGiftIds;
      if (missingGiftIds.length > 1) {
        try {
          cart = await ctx.addGift(missingGiftIds) || await ctx.read();
        } catch (error) {
          if (ctx.dryRun) throw error;
          cart = await ctx.read();
        }
        remainingGiftIds = missingGiftIds.filter(id => !cart.items.some(item => String(item.variant_id) === id));
      }
      let failed = false;
      for (const id of remainingGiftIds) {
        let added = false;
        // One retry: a gift that is still missing after it is best-effort.
        const attempts = missingGiftIds.length > 1 ? 1 : 2;
        for (let attempt = 0; attempt < attempts && !added; attempt++) {
          try {
            // Form-POST path, NOT fetch — see window.MobyCart.addGiftLine for why
            // (Shopify's pixel bridge would otherwise report an extra AddToCart).
            // No response to check: a fresh cart read is the success signal.
            // The iframe lands on /cart.js; when readable that is the cart.
            const landed = await ctx.addGift(id);
            cart = landed || await ctx.read();
            added = cart.items.some(function(item) { return String(item.variant_id) === String(id); });
            if (!added) throw new Error('Gift add failed');
          } catch (error) {
            if (ctx.dryRun) throw error;
            console.warn('Welcome Kit gift could not be added', id, error);
          }
        }
        if (!added) failed = true;
      }
      return { cart: cart, failed: failed };
    }

    function cartPriceSignature(cart) {
      const paid = (cart.items || []).filter(item => !window.MobyIsGiftLine(item));
      return JSON.stringify([cart.currency, cart.total_price, paid.map(item => [
        item.key, String(item.selling_plan_allocation?.selling_plan?.id || ''),
        item.quantity, item.final_line_price
      ])]);
    }

    function renderedPriceSignature() {
      const paid = Array.from(drawer.querySelectorAll('[data-cart-item]:not([data-gift-item])'));
      return JSON.stringify([drawer.dataset.currency, Number(drawer.dataset.cartTotalCents), paid.map(item => [
        item.dataset.lineId, item.dataset.sellingPlanId || '',
        Number(item.dataset.quantity), Number(item.dataset.lineFinalPrice)
      ])]);
    }

    // Verify the offer before gifts. Only matching server-rendered prices can
    // appear early; checkout still waits for the complete settled cart.
    async function settleSteps(ctx, cart) {
      const planGuard = await enforceCartDataPlanGuard(ctx, cart);
      if (planGuard.failed) {
        const planError = new Error('Selling plan consistency check failed');
        planError.mobyPlanConsistency = true;
        return { status: 'unverified-pricing', error: planError };
      }
      let current = planGuard.cart;
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          const offer = await ctx.applyOffer(current);
          current = offer.cart;
          if (!ctx.dryRun) {
            confirmedPriceSignature = cartPriceSignature(current);
            if (offer.sections && drawer.classList.contains('is-open')) {
              renderSeq += 1;
              await applyDrawerHtml(offer.sections, offer.writeCount);
            }
            updateCheckoutGate();
          }
          break;
        } catch (error) {
          if (ctx.dryRun) throw error;
          confirmedPriceSignature = null;
          if (error.mobyOfferSchedule) return { status: 'schedule-conflict', error: error };
          if (attempt === 1) return { status: 'unverified-pricing', error: error };
          current = await ctx.read();
        }
      }
      const gifts = await reconcileGiftLines(ctx, current);
      return { status: gifts.failed ? 'gift-warning' : 'verified', cart: gifts.cart };
    }

    // Show/hide cart error message
    function renderCartErrorState() {
      const errorEl = drawer.querySelector('[data-cart-error]');
      const errorText = drawer.querySelector('[data-cart-error-text]');
      if (errorEl && errorText) {
        errorText.textContent = checkoutErrorMessage;
        errorEl.hidden = !checkoutErrorMessage;
      }
    }

    function showCartError(message, persistent) {
      const errorEl = drawer.querySelector('[data-cart-error]');
      checkoutErrorMessage = message;
      checkoutErrorPersistent = Boolean(persistent);
      renderCartErrorState();
      if (errorEl) {
        if (errorEl.mobyHideTimer) clearTimeout(errorEl.mobyHideTimer);
        if (!checkoutErrorPersistent) {
          errorEl.mobyHideTimer = setTimeout(() => hideCartError(), 5000);
        }
      }
    }

    function hideCartError() {
      const errorEl = drawer.querySelector('[data-cart-error]');
      checkoutErrorMessage = '';
      checkoutErrorPersistent = false;
      if (errorEl) {
        if (errorEl.mobyHideTimer) clearTimeout(errorEl.mobyHideTimer);
        errorEl.mobyHideTimer = null;
        errorEl.hidden = true;
      }
    }

    function cartConsistencyMessage(error) {
      if (error.mobyOfferSchedule) return error.message;
      if (error.mobyPlanConsistency) {
        return drawer.dataset.textPlanSwitchFailed || 'Could not update your subscription discount — please adjust the quantity again.';
      }
      return drawer.dataset.textCartVerificationFailed || 'Could not verify your cart. Please try again.';
    }

    function syncDrawerDataset(newDrawer) {
      if (!newDrawer) return;
      Array.from(newDrawer.attributes).forEach(function(attribute) {
        if (attribute.name.indexOf('data-') === 0) {
          drawer.setAttribute(attribute.name, attribute.value);
        }
      });
    }

    function preserveShippingStatus(newDrawer) {
      if (!newDrawer) return;
      const currentBar = drawer.querySelector('[data-shipping-bar]');
      const nextBar = newDrawer.querySelector('[data-shipping-bar]');
      const currentText = drawer.querySelector('[data-shipping-text]');
      const nextText = newDrawer.querySelector('[data-shipping-text]');
      const currentFill = drawer.querySelector('[data-shipping-fill]');
      const nextFill = newDrawer.querySelector('[data-shipping-fill]');

      if (currentText && nextText && currentText.textContent.trim()) {
        nextText.textContent = currentText.textContent;
      }
      if (currentFill && nextFill) {
        nextFill.style.width = currentFill.style.width;
      }
      if (currentBar && nextBar) {
        nextBar.style.display = currentBar.style.display;
      }
    }

    // Refresh cart drawer content via Section Rendering API
    // Swap in freshly rendered drawer HTML. `writeTag` is the coordinator's
    // write count the HTML reflects; checkout stays gated until the drawer
    // shows a render taken after the last cart write.
    async function applyDrawerHtml(html, writeTag) {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newSection = doc.getElementById('cart-drawer-section');
      if (newSection && sectionWrapper) {
        // Update thresholds and currency from new data
        if (newSection.dataset.shippingThresholds) {
          try {
            shippingThresholds = JSON.parse(newSection.dataset.shippingThresholds);
          } catch (e) {}
        }
        if (newSection.dataset.currency) {
          currentCurrency = newSection.dataset.currency;
        }
        currentCountry = (newSection.dataset.countryCode || '').toUpperCase();
      }
      const newDrawer = doc.getElementById('moby-cart-drawer');
      if (!newDrawer) return false;
      const focusState = captureDrawerFocusState();
      syncDrawerDataset(newDrawer);
      preserveShippingStatus(newDrawer);
      drawer.innerHTML = newDrawer.innerHTML;
      if (writeTag != null) {
        renderedWriteCount = writeTag;
        renderFailed = false;
      }
      updateCheckoutGate();
      renderCartErrorState();
      renderSubscriptionSwitch();
      renderQuarantine();
      // Event delegation means we don't need to re-attach handlers
      if (drawer.classList.contains('is-open')) {
        await applyDrawerFocusTrap(false);
        restoreDrawerFocusState(focusState);
      }
      return true;
    }

    // Render the drawer section (read-only: no cart writes). A response that
    // lands after a newer render started is discarded, so an older render can
    // never replace the verified drawer or ungate checkout.
    async function refreshCartDrawer(showLoading = true) {
      const seq = ++renderSeq;
      const state = window.MobyCart.state();
      // A render that starts while a write is still in flight may predate that
      // write's effect, so it never counts as current.
      const writeTag = state.writesInFlight > 0 ? -1 : state.writeCount;
      renderingWriteCount = writeTag;
      try {
        if (showLoading) showSkeleton();
        const response = await fetch((window.Shopify?.routes?.root || '/') + '?section_id=moby-cart-drawer');
        if (!response.ok) throw new Error('Cart render failed');
        const html = await response.text();
        if (seq !== renderSeq) return false;
        // A 200 response without the drawer is a failed render (recoverable via Retry).
        if (!await applyDrawerHtml(html, writeTag)) throw new Error('Cart render incomplete');
        // Only a settled cart with no write after it describes this render; an
        // older one (e.g. the startup empty cart during a purchase) would
        // overwrite the freshly rendered count. The next settle updates it.
        const settled = window.MobyCart.lastSettled();
        if (settled && settled.cart) {
          if (settled.writeCount === window.MobyCart.state().writeCount) updateCartSummary(settled.cart);
        } else {
          updateCartSummary(await window.MobyCart.get());
        }
        return true;
      } catch (error) {
        console.error('Error refreshing cart drawer:', error);
        if (seq !== renderSeq) return false;
        renderFailed = true;
        checkoutConsistencyError = true;
        showCartError(cartConsistencyMessage(error), true);
        return false;
      } finally {
        if (seq === renderSeq) {
          renderingWriteCount = null;
          hideSkeleton();
          updateCheckoutGate();
        }
      }
    }

    function updateCartSummary(cart) {
      if (!cart) return;
      currentCurrency = cart.currency || currentCurrency;
      updatePerServingPrices();
      const visibleCount = window.MobyVisibleCount ? window.MobyVisibleCount(cart) : cart.item_count;
      updateShippingBar(cart.total_price, visibleCount, cart);
      updateThresholdUpsell(cart);
      updateHeaderCartCount(visibleCount);
    }

    // Update cart count badge in header
    function updateHeaderCartCount(count) {
      const countBadges = document.querySelectorAll('[data-cart-count]');
      countBadges.forEach(badge => {
        badge.textContent = count;
        badge.style.display = count > 0 ? 'flex' : 'none';
      });

      // Also update the count text in drawer
      const countText = drawer.querySelector('[data-cart-count-text]');
      if (countText) {
        var template = (drawer && drawer.dataset.textItemsInCart) || '{count} items in your cart';
        countText.textContent = template.replace('{count}', count).replace('COUNT_PLACEHOLDER', count);
      }
    }

    // Open drawer function
    async function openCartDrawer() {
      drawerOpener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
      // A cached "cart is empty" render is stale once a write, purchase or
      // settle has happened since it was taken: show the existing skeleton
      // (before the drawer paints) instead of a false empty cart and 0 count.
      // A genuinely empty cart and an already rendered cart open as before;
      // the next render replaces this markup (or hideSkeleton on error).
      const openState = window.MobyCart.state();
      const staleEmpty = Boolean(drawer.querySelector('.moby-cart-drawer__empty')) &&
        (openState.busy || openState.settling || openState.purchasePending || openState.unsettledWrites || renderedWriteCount !== openState.writeCount);
      if (staleEmpty) {
        showSkeleton();
        const countText = drawer.querySelector('[data-cart-count-text]');
        if (countText) countText.style.visibility = 'hidden';
      }
      // Open drawer immediately for snappy UX; content follows the cart state.
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('cart-drawer-open');
      setPageInert(true);
      updateCheckoutGate();
      // Show the current cart right away; while it is still settling the
      // checkout stays gated and the final cart re-renders when it is done.
      void refreshCartDrawer(false);
      await applyDrawerFocusTrap(true);
    }

    // Close drawer function
    function closeCartDrawer() {
      // Release focus trap
      focusModule?.removeTrapFocus();
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('cart-drawer-open');
      setPageInert(false);
      restoreFocusToOpener();
    }

    // Expose globally
    window.openCartDrawer = openCartDrawer;
    window.closeCartDrawer = closeCartDrawer;
    window.refreshCartDrawer = refreshCartDrawer;
    window.updateHeaderCartCount = updateHeaderCartCount;

    // Plan guard from cart data (settle step): a 1-box plan with 3 boxes moves
    // to the quarterly plan, a quarterly line below 3 boxes moves to the
    // monthly plan, and retired calendar plans migrate. Writes only via ctx.
    async function enforceCartDataPlanGuard(ctx, cart) {
      const resolvedPlanIds = resolveSellingPlanIds();
      const plan3boxId = resolvedPlanIds.plan3boxId;
      const plan1boxId = resolvedPlanIds.plan1boxId;
      const eligibleProductId = drawer.dataset.eligibleProductId;
      if (!cart || !Array.isArray(cart.items) || !plan3boxId || !plan1boxId || !eligibleProductId) {
        return { cart: cart, changed: false, failed: false };
      }

      let changed = false;
      for (let index = 0; index < cart.items.length; index++) {
        const item = cart.items[index];
        if (String(item.product_id) !== String(eligibleProductId)) continue;
        const currentPlanId = item.selling_plan_allocation &&
          item.selling_plan_allocation.selling_plan &&
          item.selling_plan_allocation.selling_plan.id;
        let targetPlanId = null;
        if (currentPlanId && !resolvedPlanIds.singleBoxPlanIds.has(String(currentPlanId)) && String(currentPlanId) !== String(plan3boxId)) {
          targetPlanId = item.quantity >= 3 ? plan3boxId : plan1boxId;
        } else if (String(currentPlanId) === String(plan3boxId) && item.quantity < 3) {
          targetPlanId = plan1boxId;
        } else if (String(currentPlanId) === String(plan1boxId) && item.quantity === 3) {
          targetPlanId = plan3boxId;
        }
        if (!targetPlanId) continue;

        const properties = { ...(item.properties || {}), _moby_offer: 'calendar-2026-10' };
        const effect = window.MobyCart.lineEffect({ id: item.variant_id, selling_plan: targetPlanId, properties: properties }, item.quantity, true);
        let ok = false;
        // Retry once only after a known rejection; a lost response is held by
        // the coordinator until its effect is seen.
        for (let attempt = 0; attempt < 2 && !ok; attempt++) {
          try {
            await ctx.post('change', {
              line: index + 1,
              quantity: item.quantity,
              selling_plan: parseInt(targetPlanId, 10),
              properties: properties
            }, effect);
            ok = true;
          } catch (error) {
            if (!error.mobyRejected) throw error;
          }
        }
        if (!ok) return { cart: cart, changed: changed, failed: true };
        changed = true;
        // Line indexes can shift after a plan change; continue on a fresh read.
        cart = await ctx.read();
        index = -1;
      }
      return { cart: cart, changed: changed, failed: false };
    }

    // Update cart quantity/plan — one change.js through the coordinator. The
    // response's section HTML is shown provisionally (checkout stays gated);
    // the settle job then applies plan guard, gifts and offer and renders the
    // final cart.
    async function updateCartQuantity(line, quantity, sellingPlan, clearPlan) {
      const item = drawer.querySelector('[data-cart-item][data-line-index="' + parseInt(line) + '"]');
      // Every write needs an observable effect in case its response is lost.
      if (!item) return;
      const currentProperties = JSON.parse(item.dataset.itemProperties || '{}');
      const payload = {
        line: parseInt(line),
        quantity: quantity,
        sections: 'moby-cart-drawer'
      };
      let properties = currentProperties;
      let planForEffect = item.dataset.sellingPlanId || '';
      if (sellingPlan) {
        payload.selling_plan = parseInt(sellingPlan, 10);
        properties = { ...currentProperties, _moby_offer: 'calendar-2026-10' };
        payload.properties = properties;
        planForEffect = String(payload.selling_plan);
      }
      // Explicit null clears the plan, turning a subscription line back
      // into a one-time purchase.
      else if (clearPlan) {
        payload.selling_plan = null;
        planForEffect = '';
      }
      const effect = quantity === 0
        ? { type: 'key-absent', key: item.dataset.lineId }
        : window.MobyCart.lineEffect({ id: item.dataset.variantId, selling_plan: planForEffect, properties: properties }, quantity, true);

      let data;
      let writeTagAfterChange = null;
      try {
        data = await window.MobyCart.exclusive(function(ctx) {
          return ctx.post('change', payload, effect).then(function(result) {
            writeTagAfterChange = window.MobyCart.state().writeCount;
            return result;
          });
        });
        hideCartError();
      } catch (error) {
        if (error.mobyQuarantined) return;
        const errorMessage = error.message || 'Could not update quantity';
        console.error('Cart update error:', errorMessage);
        checkoutConsistencyError = true;
        showCartError(errorMessage, true);
        void window.MobyCart.requestSettle({ force: true });
        throw error;
      }

      // Immediate view from the atomic response while the settle runs.
      if (data && data.sections && data.sections['moby-cart-drawer']) {
        renderSeq += 1;
        await applyDrawerHtml(data.sections['moby-cart-drawer'], writeTagAfterChange);
      }
      await window.MobyCart.requestSettle();
    }

    // Initialize event listeners using event delegation (works after innerHTML changes)
    // Centralized processing guard — ensures isProcessing is ALWAYS reset (C1-Cart fix)
    async function processCartAction(action) {
      if (isProcessing) return;
      confirmedPriceSignature = null;
      isProcessing = true;
      updateCheckoutGate();
      try {
        await action();
      } catch (error) {
        console.error('Cart action error:', error);
      } finally {
        isProcessing = false;
        updateCheckoutGate();
      }
    }

    function initCartDrawerEvents() {
      // Choosing a frequency is what applies (or changes) the selling plan.
      // The subscribe row only reveals this select — it does not commit a
      // default 4-week plan on tap.
      drawer.addEventListener('change', async function(e) {
        const cadence = e.target.closest('[data-cadence-select]');
        if (!cadence || !cadence.value) return;
        const item = cadence.closest('[data-cart-item]');
        if (!item) return;
        const qtyValue = item.querySelector('[data-quantity-value]');
        const qty = qtyValue ? (parseInt(qtyValue.textContent) || 1) : 1;
        const row = cadence.closest('[data-cadence-row]');
        if (row) row.classList.remove('is-picking');
        await processCartAction(() => updateCartQuantity(cadence.dataset.line, qty, cadence.value));
      });

      // Use event delegation on drawer - attach once, works for all dynamic content
      drawer.addEventListener('click', async function(e) {
        // Close button
        if (e.target.closest('[data-cart-drawer-close]')) {
          closeCartDrawer();
          return;
        }

        const checkoutLink = e.target.closest('[data-cart-checkout]');
        if (checkoutLink && checkoutGatePending()) {
          e.preventDefault();
          return;
        }
        if (checkoutLink && checkoutConsistencyError) {
          e.preventDefault();
          void retryCheckoutConsistency(checkoutLink);
          return;
        }

        // Prevent double-clicks while processing
        if (isProcessing) {
          e.preventDefault();
          return;
        }

        // Quantity minus
        const minusBtn = e.target.closest('[data-quantity-minus]');
        if (minusBtn) {
          e.preventDefault();
          const line = minusBtn.dataset.line;
          const item = minusBtn.closest('[data-cart-item]');
          if (!item) return;
          const qtyValue = item.querySelector('[data-quantity-value]');
          if (!qtyValue) return;
          const currentQty = parseInt(qtyValue.textContent) || 1;

          await processCartAction(() =>
            updateCartQuantity(line, currentQty > 1 ? currentQty - 1 : 0)
          );
          return;
        }

        // Quantity plus
        const plusBtn = e.target.closest('[data-quantity-plus]');
        if (plusBtn) {
          e.preventDefault();

          if (plusBtn.disabled || plusBtn.classList.contains('is-disabled')) return;

          const line = plusBtn.dataset.line;
          const item = plusBtn.closest('[data-cart-item]');
          if (!item) return;
          const qtyValue = item.querySelector('[data-quantity-value]');
          if (!qtyValue) return;
          const currentQty = parseInt(qtyValue.textContent) || 1;

          // Check max quantity from data attribute
          const maxQty = parseInt(item.dataset.maxQuantity) || 999;
          if (currentQty >= maxQty) {
            plusBtn.classList.add('is-disabled');
            return;
          }

          await processCartAction(() =>
            updateCartQuantity(line, currentQty + 1)
          );
          return;
        }

        // Remove item
        const removeBtn = e.target.closest('[data-remove-item]');
        if (removeBtn) {
          e.preventDefault();
          const line = removeBtn.dataset.line;
          await processCartAction(() => updateCartQuantity(line, 0));
          return;
        }

        // Quantity offer inside the one-time option. Runs before the
        // option handler below so a tap on the button adds a box instead
        // of also re-selecting the row it sits in.
        const qtyOfferBtn = e.target.closest('[data-qty-offer-btn]');
        if (qtyOfferBtn) {
          e.preventDefault();
          e.stopPropagation();
          const item = qtyOfferBtn.closest('[data-cart-item]');
          if (!item) return;
          const qtyValue = item.querySelector('[data-quantity-value]');
          const qty = qtyValue ? (parseInt(qtyValue.textContent) || 1) : 1;
          const maxQty = parseInt(item.dataset.maxQuantity) || 999;
          const target = Math.min(qty + 1, maxQty);
          if (target === qty) return;
          await processCartAction(() =>
            updateCartQuantity(qtyOfferBtn.dataset.line, target)
          );
          return;
        }

        // Purchase-type choice: selecting subscribe reveals cadence first.
        // The plan is applied only after the shopper picks a frequency
        // (or immediately when the tier is fixed to every 12 weeks).
        const upgradeSubBtn = e.target.closest('[data-upgrade-to-sub]');
        if (upgradeSubBtn) {
          e.preventDefault();
          const item = upgradeSubBtn.closest('[data-cart-item]');
          if (!item) return;
          const choice = upgradeSubBtn.closest('.moby-cart-drawer__choice');
          const cadence = choice && choice.querySelector('[data-cadence-select]');
          const row = choice && choice.querySelector('[data-cadence-row]');

          if (choice) {
            choice.querySelectorAll('.moby-cart-drawer__opt').forEach(function(o) {
              const on = o === upgradeSubBtn;
              o.classList.toggle('is-selected', on);
              o.setAttribute('aria-checked', on ? 'true' : 'false');
            });
            if (row) {
              row.hidden = false;
              row.classList.add('is-picking');
            }
          }

          if (item.dataset.sellingPlanId) return;

          const line = upgradeSubBtn.dataset.line;
          const qtyValue = item.querySelector('[data-quantity-value]');
          const qty = qtyValue ? (parseInt(qtyValue.textContent) || 1) : 1;

          if (cadence) {
            if (!cadence.value) {
              cadence.focus();
              return;
            }
            if (row) row.classList.remove('is-picking');
            await processCartAction(() => updateCartQuantity(line, qty, cadence.value));
            return;
          }

          const planId = item.dataset.upgradePlanId || item.dataset.fallbackPlanId;
          if (!planId) return;
          await processCartAction(() => updateCartQuantity(line, qty, planId));
          return;
        }

        // Selecting the one-time row on a subscription line drops the plan.
        const oneTimeBtn = e.target.closest('[data-choice-onetime]');
        if (oneTimeBtn) {
          e.preventDefault();
          const item = oneTimeBtn.closest('[data-cart-item]');
          if (!item) return;
          const choice = oneTimeBtn.closest('.moby-cart-drawer__choice');
          if (choice) {
            choice.querySelectorAll('.moby-cart-drawer__opt').forEach(function(o) {
              const on = o === oneTimeBtn;
              o.classList.toggle('is-selected', on);
              o.setAttribute('aria-checked', on ? 'true' : 'false');
            });
            const reassurance = choice.querySelector('.moby-cart-drawer__reassure');
            if (reassurance) reassurance.hidden = true;
            const row = choice.querySelector('[data-cadence-row]');
            if (row) {
              row.hidden = true;
              row.classList.remove('is-picking');
            }
          }
          // Already a one-time line: nothing to change on the server.
          if (!item.dataset.sellingPlanId) return;
          const line = oneTimeBtn.dataset.line;
          const qtyValue = item.querySelector('[data-quantity-value]');
          const qty = qtyValue ? (parseInt(qtyValue.textContent) || 1) : 1;
          await processCartAction(() => updateCartQuantity(line, qty, null, true));
          return;
        }

        const thresholdUpsellBtn = e.target.closest('[data-threshold-upsell-btn]');
        if (thresholdUpsellBtn) {
          e.preventDefault();
          if (!thresholdUpsellState) return;

          thresholdUpsellBtn.disabled = true;
          await processCartAction(() =>
            updateCartQuantity(thresholdUpsellState.line, thresholdUpsellState.targetQuantity)
          );
          return;
        }
      });
    }

    // Close on escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) {
        closeCartDrawer();
      }
    });

    // Initialize on page load
    initCartDrawerEvents();

    function renderSubscriptionSwitch() {
      const notice = window.MobyCart.subscriptionSwitch?.();
      const items = drawer.querySelector('[data-cart-items-wrapper]');
      if (items) items.inert = Boolean(window.MobyCart.isMutating?.());
      const element = drawer.querySelector('[data-subscription-switch]');
      if (!element) return;
      element.hidden = !notice;
      element.querySelector('[data-subscription-switch-text]').textContent = notice ? notice.message : '';
      element.querySelector('[data-undo-subscription-switch]').hidden = !notice?.canUndo;
      element.querySelector('[data-undo-subscription-switch]').disabled = Boolean(window.MobyCart.isMutating?.() || isProcessing);
    }

    // Unresolved write: read-only review. The banner offers Check again (one
    // read) and Review cart (re-render the latest server cart). Nothing writes.
    let quarantineShown = false;
    function renderQuarantine() {
      const state = window.MobyCart.state();
      const actions = drawer.querySelector('[data-cart-quarantine-actions]');
      if (state.quarantined) {
        quarantineShown = true;
        showCartError(drawer.dataset.textQuarantine || 'We’re still confirming your last cart update.', true);
      } else if (quarantineShown) {
        quarantineShown = false;
        if (checkoutErrorPersistent) hideCartError();
      }
      if (actions) actions.hidden = !state.quarantined;
    }

    // One checkout gate for the whole cart lifecycle.
    function checkoutGatePending() {
      const state = window.MobyCart.state();
      if (state.quarantined) return !state.quarantineCheckoutAllowed;
      const renderBehind = drawer.classList.contains('is-open') && !renderFailed && renderedWriteCount !== state.writeCount;
      return Boolean(isProcessing || state.busy || state.settling || state.unsettledWrites || renderBehind);
    }
    function updateCheckoutGate() {
      const pending = checkoutGatePending();
      const state = window.MobyCart.state();
      const pricesConfirmed = confirmedPriceSignature && confirmedPriceSignature === renderedPriceSignature();
      drawer.classList.toggle('is-confirming-prices', (pending && !pricesConfirmed) || checkoutConsistencyError || state.quarantined);
      setCheckoutPending(pending);
    }

    document.addEventListener('moby:subscription-switch', renderSubscriptionSwitch);
    document.addEventListener('moby:purchase-pending', function() {
      if (window.MobyCart.state().purchasePending) confirmedPriceSignature = null;
      updateCheckoutGate();
      renderSubscriptionSwitch();
    });
    let wasQuarantined = false;
    document.addEventListener('moby:cart-state', function(event) {
      const state = event.detail || window.MobyCart.state();
      if (state.quarantined || (!state.settling && state.unsettledWrites)) confirmedPriceSignature = null;
      renderQuarantine();
      renderSubscriptionSwitch();
      updateCheckoutGate();
      // Show the cart as Shopify last reported it while a write is unresolved;
      // an unresolved purchase opens the drawer on that read-only review.
      if (state.quarantined && !wasQuarantined) {
        if (drawer.classList.contains('is-open')) void refreshCartDrawer(false);
        else if (state.purchasePending) void openCartDrawer();
      }
      wasQuarantined = state.quarantined;
    });

    document.addEventListener('moby:cart-settled', async function(event) {
      const settled = event.detail;
      if (window.MobyCart.okStatus(settled.status)) {
        checkoutConsistencyError = false;
        if (settled.cart) confirmedPriceSignature = cartPriceSignature(settled.cart);
        if (checkoutErrorPersistent && !window.MobyCart.state().quarantined) hideCartError();
        if (settled.status === 'gift-warning') {
          showCartError(drawer.dataset.textGiftReconciliationFailed || 'Could not add all Welcome Kit items. Please try again. Your paid product is still in the cart.');
        }
      } else {
        // Known settle failure (e.g. a mixed subscription cart): checkout stays
        // gated with the explanation, while the drawer controls stay usable so
        // the customer can remove an option or choose the latest plan.
        confirmedPriceSignature = null;
        checkoutConsistencyError = true;
        showCartError(cartConsistencyMessage(settled.error || {}), true);
      }
      if (settled.cart) updateCartSummary(settled.cart);
      if (settled.sections) {
        renderSeq += 1;
        // Bundled HTML without the drawer falls back to a normal render.
        if (!await applyDrawerHtml(settled.sections, settled.writeCount) && drawer.classList.contains('is-open')) void refreshCartDrawer(false);
      } else if (drawer.classList.contains('is-open') && renderedWriteCount !== settled.writeCount && renderingWriteCount !== settled.writeCount) {
        void refreshCartDrawer(false);
      }
      updateCheckoutGate();
    });

    drawer.addEventListener('click', async function(event) {
      if (event.target.closest('[data-cart-quarantine-check]')) {
        event.preventDefault();
        await window.MobyCart.checkAgain();
        return;
      }
      if (event.target.closest('[data-cart-quarantine-review]')) {
        event.preventDefault();
        await refreshCartDrawer(false);
        return;
      }
      const undo = event.target.closest('[data-undo-subscription-switch]');
      if (!undo || undo.disabled || window.MobyCart.isMutating()) return;
      undo.disabled = true;
      try {
        await window.MobyCart.undoSubscriptionSwitch();
      } catch (error) {
        showCartError(error.message || drawer.dataset.textUndoUnavailable);
      } finally {
        undo.disabled = false;
        renderSubscriptionSwitch();
      }
    });

    document.addEventListener('click', function(event) {
      const checkout = event.target.closest && event.target.closest('[data-cart-checkout], a[href="/checkout"], button[name="checkout"]');
      if (!checkout) return;
      // Block while any cart write, settle or render of the final cart is
      // pending, and during an unresolved write unless the latest read already
      // satisfies every settle invariant.
      if (checkoutGatePending()) {
        event.preventDefault();
        event.stopPropagation();
      } else if (checkoutConsistencyError && !window.MobyCart.state().quarantined) {
        event.preventDefault();
        event.stopPropagation();
        void retryCheckoutConsistency(checkout);
      }
    }, true);

    async function retryCheckoutConsistency(checkoutLink) {
      if (checkoutGatePending()) return;
      const settled = await window.MobyCart.requestSettle({ force: true });
      if (!settled || !window.MobyCart.okStatus(settled.status)) return;
      if (!await refreshCartDrawer(false)) return;
      if (checkoutConsistencyError || checkoutGatePending()) return;
      const href = checkoutLink && checkoutLink.getAttribute('href');
      window.location.assign(href || '/checkout');
    }

    // Native cart components publish cart:update; other code may publish
    // cart:updated. Both mean the cart may have changed outside the
    // coordinator, so settle again. The coordinator's own events are ignored.
    function handleExternalCartUpdate(event) {
      const source = event?.detail?.data?.source;
      if (source === 'moby-settled' || source === 'moby-offer-reconciled') return;
      void window.MobyCart.requestSettle({ force: true });
    }
    document.addEventListener('cart:update', handleExternalCartUpdate);
    document.addEventListener('cart:updated', handleExternalCartUpdate);

    window.MobyCart.registerSettleSteps(settleSteps);
    updateCheckoutGate();
    // Initial reconciliation of whatever cart the page loaded with.
    void window.MobyCart.requestSettle({ force: true });
  })();
